import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CopyIcon, RefreshCw } from 'lucide-react';
import CopyIconButton from '@/components/ui/copy-to-clipboard/copy-icon-button';
import ConfirmationDialog from '@/components/ui/dialog-wrapper/ConfirmationDialog';
import useParamWorkspaceId from '@/hook/useParamWorkspaceId';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { aiServiceKeys, getTokenOption } from '@/service/ai/hook';
import { regenerateAIServiceToken } from '@/service/ai';

const AiCredentials = () => {
  const { workspaceId } = useParamWorkspaceId();
  const queryClient = useQueryClient();
  const { data: token } = useQuery(
    getTokenOption({
      workspace_id: workspaceId,
    })
  );
  const { mutate: regenerateToken, isPending: regenerating } = useMutation({
    mutationFn: () =>
      regenerateAIServiceToken({
        workspace_id: workspaceId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [aiServiceKeys.token, workspaceId],
      });
    },
  });

  // mock regenerate function
  const handleRegenerate = () => {
    regenerateToken();
  };

  return (
    <div className="container mx-auto max-w-2xl pt-10">
      <Card>
        <CardHeader>
          <CardTitle>API Key</CardTitle>
          <CardDescription>
            This is your AI service API key. You can use it to authenticate
            requests to the API. Only one API key is available per workspace.
            You can regenerate it at any time.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mt-2 flex items-center gap-2">
            <Input
              type="text"
              value={token?.saas_token}
              readOnly
              className="font-mono text-sm"
              endIcon={<CopyIconButton text={token?.saas_token || ''} />}
            />
            <ConfirmationDialog
              title="Regenerate API Key"
              message="Are you sure you want to regenerate?"
              onConfirm={() => handleRegenerate()}
              confirmText="Regenerate"
              slots={{
                confirmButton: {
                  variant: 'secondary',
                },
              }}
            >
              <Button
                variant="secondary"
                size="icon"
                disabled={regenerating}
                title="Regenerate API Key"
              >
                <RefreshCw
                  className={regenerating ? 'h-4 w-4 animate-spin' : 'h-4 w-4'}
                />
              </Button>
            </ConfirmationDialog>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Keep your API key secure. Regenerating will invalidate the previous
            key immediately.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AiCredentials;
